import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CREATE_PROJECT_TASK,
  FETCH_GTM_PROJECT_TASKS,
  FETCH_PROJECT_TASKS,
} from "./graphql";
import SortTasksView from "./SortTasksView";
import TaskView from "./TaskView";

const TasksView = ({
  title = "Tasks",
  projectType,
  projectId,
  showProjectLink,
  pollInterval = process.env.NODE_ENV === "production" ? 5000 : 1000 * 60 * 60,
}) => {
  const [sortMode, setSortMode] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const query =
    projectType === "GTM" ? FETCH_GTM_PROJECT_TASKS : FETCH_PROJECT_TASKS;
  const { loading, error, data } = useQuery(query, {
    variables: { id: projectId },
    pollInterval,
  });
  const [createProjectTask, createProjectTaskRes] = useMutation(
    CREATE_PROJECT_TASK,
    {
      onError(error) {
        Alert("error", parseError(error));
      },
    },
  );

  if (loading)
    return (
      <div className="relative p-10">
        <InlineSpinner />
      </div>
    );

  if (error) return <Errors error={error} />;

  function addTaskHandler() {
    const name = window.prompt("Enter task name:");
    if (name) {
      createProjectTask({
        variables: { projectType, projectId, name },
      });
    }
  }

  const workingTasks = data.project.tasks
    .filter((i) => !i.completedAt)
    .sort((a, b) => a.ordering - b.ordering);
  const completedTasks = data.project.tasks
    .filter((i) => i.completedAt)
    .sort((a, b) => a.ordering - b.ordering);

  const tasksToShow = showCompletedTasks
    ? [...workingTasks, ...completedTasks]
    : workingTasks;
  const sortedTasks = tasksToShow.sort((a, b) => a.ordering - b.ordering);

  return (
    <div className="card">
      <h5>
        {showProjectLink ? (
          <Link to={`/development/projects/${projectId}`} target="_blank">
            {title}
          </Link>
        ) : (
          title
        )}
      </h5>

      {sortMode ? (
        <SortTasksView
          initialTasks={sortedTasks}
          onCompleted={() => setSortMode(false)}
        />
      ) : (
        <>
          {sortedTasks.length > 0 && (
            <div className="mt-2 -mx-2">
              <table>
                <tbody>
                  {sortedTasks.map((task) => (
                    <tr
                      key={task.id}
                      className="border-y border-gray-100 dark:border-gray-700"
                    >
                      <td>
                        <TaskView task={task} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-2 flex  space-x-3">
            <Button
              onClick={addTaskHandler}
              loading={createProjectTaskRes.loading}
            >
              + Add
            </Button>
            {sortedTasks.length > 1 && (
              <Button onClick={() => setSortMode(!sortMode)}>Sort</Button>
            )}

            {completedTasks.length > 0 && (
              <Button
                onClick={() => setShowCompletedTasks(!showCompletedTasks)}
              >
                {showCompletedTasks ? "Hide" : "Show"} Completed(
                {completedTasks.length})
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TasksView;
